import React, { useState, useRef, useEffect } from 'react'
import { Outlet, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { usePermissions } from '../hooks/usePermissions'
import { useTheme } from '../hooks/useTheme'
import NotificationBell from './NotificationBell'
import SessionExpiredModal from './SessionExpiredModal'
import logo from '../assets/logo-full-2.svg'


const Layout: React.FC = () => {
  const { user, logout, isSessionExpiredModalOpen, showSessionExpiredModal, hideSessionExpiredModal } = useAuthStore()
  const navigate = useNavigate()
  const { canAccessAdminMenu, getAdminMenuItems, permissions, loading, error, hasPermission } = usePermissions(user)
  const { theme } = useTheme()
  const [showAdminDropdown, setShowAdminDropdown] = useState(false)
  const [showErpDropdown, setShowErpDropdown] = useState(false)
  const [openErpSubmenu, setOpenErpSubmenu] = useState<string | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileOperationsOpen, setIsMobileOperationsOpen] = useState(false)
  const [isMobileErpOpen, setIsMobileErpOpen] = useState(false)
  const [openMobileErpSubmenu, setOpenMobileErpSubmenu] = useState<string | null>(null)
  const adminDropdownRef = useRef<HTMLDivElement>(null)
  const erpDropdownRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  
  // Safety: Ensure getAdminMenuItems is a function and returns an array
  const safeGetAdminMenuItems = () => {
    if (!getAdminMenuItems || typeof getAdminMenuItems !== 'function') {
      return []
    }
    try {
      const items = getAdminMenuItems()
      return Array.isArray(items) ? items : []
    } catch (err) {
      console.error('Error getting admin menu items:', err)
      return []
    }
  }
  
  // Determine logo source - use theme logo if available, otherwise default
  // Theme logos are now local assets, so they're already imported/bundled
  const logoSource = theme?.logo?.url || logo
  const logoAlt = theme?.tenant?.name || 'Zanders Group'



  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setShowAdminDropdown(false)
      }
      if (erpDropdownRef.current && !erpDropdownRef.current.contains(event.target as Node)) {
        setShowErpDropdown(false)
        setOpenErpSubmenu(null)
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src={logoSource} 
                alt={logoAlt} 
                className="h-8 w-auto"
                style={{ filter: theme?.logo ? 'none' : 'brightness(0) saturate(100%)' }}
                onError={(e) => {
                  // Fallback to default logo if theme logo fails to load
                  if (theme?.logo) {
                    (e.target as HTMLImageElement).src = logo
                  }
                }}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex lg:items-center lg:space-x-8">
              <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm font-medium">Dashboard</Link>
              <Link to="/tickets" className="text-gray-500 hover:text-gray-700 text-sm font-medium">Tickets</Link>
              <Link to="/services" className="text-gray-500 hover:text-gray-700 text-sm font-medium">Services</Link>
              <Link to="/knowledge" className="text-gray-500 hover:text-gray-700 text-sm font-medium">Knowledge Base</Link>
              
              {/* Accounting/ERP Dropdown */}
              {hasPermission('erp', 'read') && (
                <div className="relative" ref={erpDropdownRef}>
                  <button
                    onClick={() => setShowErpDropdown(!showErpDropdown)}
                    className="text-gray-500 hover:text-gray-700 flex items-center space-x-1 text-sm font-medium"
                  >
                    <span>Accounting</span>
                    <svg className={`w-4 h-4 transition-transform ${showErpDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showErpDropdown && (
                    <div className="absolute left-0 z-20 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" role="menu" aria-orientation="vertical" tabIndex={-1}>
                      <div className="py-1" role="none">
                        {/* Core Accounting */}
                        <div 
                          className="relative group"
                          onMouseEnter={() => setOpenErpSubmenu('core')}
                          onMouseLeave={() => setOpenErpSubmenu(null)}
                        >
                          <button
                            type="button"
                            className="w-full text-left text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                          >
                            <span>Core Accounting</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {openErpSubmenu === 'core' && (
                            <div className="absolute left-full top-0 ml-0 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                              <div className="py-1">
                                <Link 
                                  to="/erp/chart-of-accounts" 
                                  className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                  onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                >
                                  Chart of Accounts
                                </Link>
                                <Link 
                                  to="/erp/journal-entries" 
                                  className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                  onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                >
                                  Journal Entries
                                </Link>
                                {hasPermission('erp_journal_entry', 'read') && (
                                  <Link 
                                    to="/erp/recurring-journal-entries" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Recurring Journal Entries
                                  </Link>
                                )}
                                {hasPermission('erp_financial_report', 'read') && (
                                  <Link 
                                    to="/erp/opening-balances" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Opening Balances
                                  </Link>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Sales & Invoicing */}
                        <div 
                          className="relative group"
                          onMouseEnter={() => setOpenErpSubmenu('sales')}
                          onMouseLeave={() => setOpenErpSubmenu(null)}
                        >
                          <button
                            type="button"
                            className="w-full text-left text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                          >
                            <span>Sales & Invoicing</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {openErpSubmenu === 'sales' && (
                            <div className="absolute left-full top-0 ml-0 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                              <div className="py-1">
                                <Link 
                                  to="/erp/invoices" 
                                  className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                  onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                >
                                  Invoices
                                </Link>
                                {hasPermission('erp_invoice', 'read') && (
                                  <Link 
                                    to="/erp/recurring-invoices" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Recurring Invoices
                                  </Link>
                                )}
                                <Link 
                                  to="/erp/payments" 
                                  className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                  onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                >
                                  Payments
                                </Link>
                                {hasPermission('erp_payment', 'read') && (
                                  <Link 
                                    to="/erp/payment-reconciliation" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Payment Reconciliation
                                  </Link>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Purchases */}
                        {hasPermission('erp_purchase_invoice', 'read') && (
                          <div 
                            className="relative group"
                            onMouseEnter={() => setOpenErpSubmenu('purchases')}
                            onMouseLeave={() => setOpenErpSubmenu(null)}
                          >
                            <button
                              type="button"
                              className="w-full text-left text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                            >
                              <span>Purchases</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openErpSubmenu === 'purchases' && (
                              <div className="absolute left-full top-0 ml-0 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                                <div className="py-1">
                                  <Link 
                                    to="/erp/purchase-invoices" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Purchase Invoices
                                  </Link>
                                  {hasPermission('erp_purchase_invoice', 'read') && (
                                    <Link 
                                      to="/erp/recurring-purchase-invoices" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Recurring Purchase Invoices
                                    </Link>
                                  )}
                                  <Link 
                                    to="/erp/suppliers" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Suppliers
                                  </Link>
                                  <Link 
                                    to="/erp/purchase-orders" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Purchase Orders
                                  </Link>
                                  <Link 
                                    to="/erp/grns" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    GRNs (Goods Receipt)
                                  </Link>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Expenses */}
                        <Link 
                          to="/erp/expenses" 
                          className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                          role="menuitem" 
                          onClick={() => setShowErpDropdown(false)}
                        >
                          Expenses
                        </Link>

                        {/* Reports & Analytics */}
                        {(hasPermission('erp', 'reports') || hasPermission('erp_financial_report', 'read')) && (
                          <div 
                            className="relative group"
                            onMouseEnter={() => setOpenErpSubmenu('reports')}
                            onMouseLeave={() => setOpenErpSubmenu(null)}
                          >
                            <button
                              type="button"
                              className="w-full text-left text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                            >
                              <span>Reports & Analytics</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openErpSubmenu === 'reports' && (
                              <div className="absolute left-full top-0 ml-0 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                                <div className="py-1">
                                  {hasPermission('erp', 'reports') && (
                                    <Link 
                                      to="/erp/reports" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Financial Reports
                                    </Link>
                                  )}
                                  {hasPermission('erp_financial_report', 'read') && (
                                    <>
                                      <Link 
                                        to="/erp/multi-currency-reports" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Multi-Currency Reports
                                      </Link>
                                      <Link 
                                        to="/erp/tax-reports" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Tax Reports
                                      </Link>
                                      <Link 
                                        to="/erp/profitability-reports" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Profitability Reports
                                      </Link>
                                      <Link 
                                        to="/erp/financial-analytics" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Financial Analytics
                                      </Link>
                                    </>
                                  )}
                                  {hasPermission('erp', 'reports') && (
                                    <Link 
                                      to="/erp/scheduled-reports" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Scheduled Reports
                                    </Link>
                                  )}
                                  {hasPermission('erp_financial_report', 'read') && (
                                    <>
                                      <Link 
                                        to="/erp/custom-report-templates" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Custom Report Templates
                                      </Link>
                                      <Link 
                                        to="/erp/budgets" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Budgets
                                      </Link>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Tax Management */}
                        {(hasPermission('erp_tax_rate', 'read') || hasPermission('erp_tax_template', 'read')) && (
                          <div 
                            className="relative group"
                            onMouseEnter={() => setOpenErpSubmenu('tax')}
                            onMouseLeave={() => setOpenErpSubmenu(null)}
                          >
                            <button
                              type="button"
                              className="w-full text-left text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                            >
                              <span>Tax Management</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openErpSubmenu === 'tax' && (
                              <div className="absolute left-full top-0 ml-0 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                                <div className="py-1">
                                  {hasPermission('erp_tax_rate', 'read') && (
                                    <>
                                      <Link 
                                        to="/erp/tax-rates" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Tax Rates
                                      </Link>
                                      <Link 
                                        to="/erp/tax-groups" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Tax Groups
                                      </Link>
                                      <Link 
                                        to="/erp/tax-exemptions" 
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                        onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                      >
                                        Tax Exemptions
                                      </Link>
                                    </>
                                  )}
                                  {hasPermission('erp_tax_template', 'read') && (
                                    <Link 
                                      to="/erp/tax-templates" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Tax Templates
                                    </Link>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Banking */}
                        {(hasPermission('erp_bank_reconciliation', 'read') || hasPermission('erp_exchange_rate', 'read')) && (
                          <div 
                            className="relative group"
                            onMouseEnter={() => setOpenErpSubmenu('banking')}
                            onMouseLeave={() => setOpenErpSubmenu(null)}
                          >
                            <button
                              type="button"
                              className="w-full text-left text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                            >
                              <span>Banking</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openErpSubmenu === 'banking' && (
                              <div className="absolute left-full top-0 ml-0 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                                <div className="py-1">
                                  {hasPermission('erp_bank_reconciliation', 'read') && (
                                    <Link 
                                      to="/erp/bank-reconciliation" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Bank Reconciliation
                                    </Link>
                                  )}
                                  {hasPermission('erp_exchange_rate', 'read') && (
                                    <Link 
                                      to="/erp/exchange-rates" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Exchange Rates
                                    </Link>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Setup & Configuration */}
                        <div 
                          className="relative group"
                          onMouseEnter={() => setOpenErpSubmenu('setup')}
                          onMouseLeave={() => setOpenErpSubmenu(null)}
                        >
                          <button
                            type="button"
                            className="w-full text-left text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                          >
                            <span>Setup & Configuration</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {openErpSubmenu === 'setup' && (
                            <div className="absolute left-full top-0 ml-0 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                              <div className="py-1">
                                {hasPermission('erp_account', 'write') && (
                                  <Link 
                                    to="/setup-wizard" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 font-medium" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    ERP Setup Wizard
                                  </Link>
                                )}
                                <div className="border-t border-gray-200 my-1"></div>
                                {hasPermission('erp_cost_center', 'read') && (
                                  <Link 
                                    to="/erp/cost-centers" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Cost Centers
                                  </Link>
                                )}
                                {hasPermission('erp_accounting_period', 'read') && (
                                  <Link 
                                    to="/erp/accounting-periods" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Accounting Periods
                                  </Link>
                                )}
                                {hasPermission('erp_financial_report', 'read') && (
                                  <>
                                    <Link 
                                      to="/erp/document-number-series" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Document Number Series
                                    </Link>
                                    <Link 
                                      to="/erp/print-formats" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Print Formats
                                    </Link>
                                    <Link 
                                      to="/erp/email-templates" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Email Templates
                                    </Link>
                                  </>
                                )}
                                {hasPermission('erp_account', 'write') && (
                                  <Link 
                                    to="/erp/data-import" 
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                    onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                  >
                                    Data Import
                                  </Link>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Advanced Features */}
                        {(hasPermission('erp_project', 'read') || hasPermission('erp_asset', 'read') || hasPermission('erp_payroll', 'read') || hasPermission('erp_subscription', 'read') || hasPermission('erp_commission', 'read')) && (
                          <div 
                            className="relative group"
                            onMouseEnter={() => setOpenErpSubmenu('advanced')}
                            onMouseLeave={() => setOpenErpSubmenu(null)}
                          >
                            <button
                              type="button"
                              className="w-full text-left text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                            >
                              <span>Advanced Features</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openErpSubmenu === 'advanced' && (
                              <div className="absolute left-full top-0 ml-0 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                                <div className="py-1">
                                  {hasPermission('erp_project', 'read') && (
                                    <Link 
                                      to="/erp/projects" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Projects
                                    </Link>
                                  )}
                                  {hasPermission('erp_asset', 'read') && (
                                    <Link 
                                      to="/erp/assets" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Assets
                                    </Link>
                                  )}
                                  {hasPermission('erp_payroll', 'read') && (
                                    <Link 
                                      to="/erp/payroll" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Payroll
                                    </Link>
                                  )}
                                  {hasPermission('erp_subscription', 'read') && (
                                    <Link 
                                      to="/erp/subscriptions" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Subscriptions
                                    </Link>
                                  )}
                                  {hasPermission('erp_commission', 'read') && (
                                    <Link 
                                      to="/erp/commissions" 
                                      className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                                      onClick={() => { setShowErpDropdown(false); setOpenErpSubmenu(null); }}
                                    >
                                      Commissions
                                    </Link>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Operations Dropdown */}
              {canAccessAdminMenu() && (
                <div className="relative" ref={adminDropdownRef}>
                  <button
                    onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                    className="text-gray-500 hover:text-gray-700 flex items-center space-x-1 text-sm font-medium"
                  >
                    <span>Operations</span>
                    <svg className={`w-4 h-4 transition-transform ${showAdminDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showAdminDropdown && (
                    <div className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabIndex={-1}>
                      <div className="py-1" role="none">
                        {safeGetAdminMenuItems().map((item) => (
                          <Link 
                            key={item.to}
                            to={item.to} 
                            className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" 
                            role="menuitem" 
                            tabIndex={-1}
                            onClick={() => setShowAdminDropdown(false)}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <Link to="/profile" className="text-gray-500 hover:text-gray-700 text-sm font-medium">Profile</Link>
            </nav>
            
            {/* Desktop User Info & Actions */}
            <div className="hidden lg:flex lg:items-center lg:space-x-4">
              <NotificationBell />
              <div className="text-sm text-gray-700">
                <span className="font-medium">{user?.firstName} {user?.lastName}</span>
                <span className="text-gray-500 ml-2">({user?.roles.join(', ')})</span>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                Logout
              </button>
            </div>

            {/* Tablet/Mobile Menu Button - Show on screens smaller than lg */}
            <div className="lg:hidden flex items-center space-x-2">
              <NotificationBell />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-500 hover:text-gray-700 p-2"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div ref={mobileMenuRef} className="lg:hidden border-t border-gray-200 py-4">
              <nav className="space-y-2">
                <Link 
                  to="/" 
                  className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/tickets" 
                  className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Tickets
                </Link>
                <Link 
                  to="/services" 
                  className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Services
                </Link>
                <Link 
                  to="/knowledge" 
                  className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Knowledge Base
                </Link>
                {/* Mobile Accounting/ERP Menu */}
                {hasPermission('erp', 'read') && (
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <button
                      onClick={() => setIsMobileErpOpen(!isMobileErpOpen)}
                      className="w-full flex items-center justify-between px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <span className="text-sm font-medium">Accounting</span>
                      <svg 
                        className={`w-4 h-4 transition-transform ${isMobileErpOpen ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isMobileErpOpen && (
                      <div className="pl-6 space-y-1">
                        {/* Core Accounting */}
                        <div>
                          <button
                            onClick={() => setOpenMobileErpSubmenu(openMobileErpSubmenu === 'core' ? null : 'core')}
                            className="w-full flex items-center justify-between py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                          >
                            <span className="font-medium">Core Accounting</span>
                            <svg className={`w-4 h-4 transition-transform ${openMobileErpSubmenu === 'core' ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {openMobileErpSubmenu === 'core' && (
                            <div className="pl-4 space-y-1">
                              <Link 
                                to="/erp/chart-of-accounts" 
                                className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                Chart of Accounts
                              </Link>
                              <Link 
                                to="/erp/journal-entries" 
                                className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                Journal Entries
                              </Link>
                              {hasPermission('erp_journal_entry', 'read') && (
                                <Link 
                                  to="/erp/recurring-journal-entries" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Recurring Journal Entries
                                </Link>
                              )}
                              {hasPermission('erp_financial_report', 'read') && (
                                <Link 
                                  to="/erp/opening-balances" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Opening Balances
                                </Link>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Sales & Invoicing */}
                        <div>
                          <button
                            onClick={() => setOpenMobileErpSubmenu(openMobileErpSubmenu === 'sales' ? null : 'sales')}
                            className="w-full flex items-center justify-between py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                          >
                            <span className="font-medium">Sales & Invoicing</span>
                            <svg className={`w-4 h-4 transition-transform ${openMobileErpSubmenu === 'sales' ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {openMobileErpSubmenu === 'sales' && (
                            <div className="pl-4 space-y-1">
                              <Link 
                                to="/erp/invoices" 
                                className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                Invoices
                              </Link>
                              {hasPermission('erp_invoice', 'read') && (
                                <Link 
                                  to="/erp/recurring-invoices" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Recurring Invoices
                                </Link>
                              )}
                              <Link 
                                to="/erp/payments" 
                                className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                Payments
                              </Link>
                              {hasPermission('erp_payment', 'read') && (
                                <Link 
                                  to="/erp/payment-reconciliation" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Payment Reconciliation
                                </Link>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Purchases */}
                        {hasPermission('erp_purchase_invoice', 'read') && (
                          <div>
                            <button
                              onClick={() => setOpenMobileErpSubmenu(openMobileErpSubmenu === 'purchases' ? null : 'purchases')}
                              className="w-full flex items-center justify-between py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                            >
                              <span className="font-medium">Purchases</span>
                              <svg className={`w-4 h-4 transition-transform ${openMobileErpSubmenu === 'purchases' ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openMobileErpSubmenu === 'purchases' && (
                              <div className="pl-4 space-y-1">
                                <Link 
                                  to="/erp/purchase-invoices" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Purchase Invoices
                                </Link>
                                {hasPermission('erp_purchase_invoice', 'read') && (
                                  <Link 
                                    to="/erp/recurring-purchase-invoices" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Recurring Purchase Invoices
                                  </Link>
                                )}
                                <Link 
                                  to="/erp/suppliers" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Suppliers
                                </Link>
                                <Link 
                                  to="/erp/purchase-orders" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Purchase Orders
                                </Link>
                                <Link 
                                  to="/erp/grns" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  GRNs (Goods Receipt)
                                </Link>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Expenses */}
                        <Link 
                          to="/erp/expenses" 
                          className="block py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Expenses
                        </Link>

                        {/* Reports & Analytics */}
                        {(hasPermission('erp', 'reports') || hasPermission('erp_financial_report', 'read')) && (
                          <div>
                            <button
                              onClick={() => setOpenMobileErpSubmenu(openMobileErpSubmenu === 'reports' ? null : 'reports')}
                              className="w-full flex items-center justify-between py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                            >
                              <span className="font-medium">Reports & Analytics</span>
                              <svg className={`w-4 h-4 transition-transform ${openMobileErpSubmenu === 'reports' ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openMobileErpSubmenu === 'reports' && (
                              <div className="pl-4 space-y-1">
                                {hasPermission('erp', 'reports') && (
                                  <Link 
                                    to="/erp/reports" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Financial Reports
                                  </Link>
                                )}
                                {hasPermission('erp_financial_report', 'read') && (
                                  <>
                                    <Link 
                                      to="/erp/multi-currency-reports" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Multi-Currency Reports
                                    </Link>
                                    <Link 
                                      to="/erp/tax-reports" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Tax Reports
                                    </Link>
                                    <Link 
                                      to="/erp/profitability-reports" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Profitability Reports
                                    </Link>
                                    <Link 
                                      to="/erp/financial-analytics" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Financial Analytics
                                    </Link>
                                  </>
                                )}
                                {hasPermission('erp', 'reports') && (
                                  <Link 
                                    to="/erp/scheduled-reports" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Scheduled Reports
                                  </Link>
                                )}
                                {hasPermission('erp_financial_report', 'read') && (
                                  <>
                                    <Link 
                                      to="/erp/custom-report-templates" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Custom Report Templates
                                    </Link>
                                    <Link 
                                      to="/erp/budgets" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Budgets
                                    </Link>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Tax Management */}
                        {(hasPermission('erp_tax_rate', 'read') || hasPermission('erp_tax_template', 'read')) && (
                          <div>
                            <button
                              onClick={() => setOpenMobileErpSubmenu(openMobileErpSubmenu === 'tax' ? null : 'tax')}
                              className="w-full flex items-center justify-between py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                            >
                              <span className="font-medium">Tax Management</span>
                              <svg className={`w-4 h-4 transition-transform ${openMobileErpSubmenu === 'tax' ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openMobileErpSubmenu === 'tax' && (
                              <div className="pl-4 space-y-1">
                                {hasPermission('erp_tax_rate', 'read') && (
                                  <>
                                    <Link 
                                      to="/erp/tax-rates" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Tax Rates
                                    </Link>
                                    <Link 
                                      to="/erp/tax-groups" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Tax Groups
                                    </Link>
                                    <Link 
                                      to="/erp/tax-exemptions" 
                                      className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      Tax Exemptions
                                    </Link>
                                  </>
                                )}
                                {hasPermission('erp_tax_template', 'read') && (
                                  <Link 
                                    to="/erp/tax-templates" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Tax Templates
                                  </Link>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Banking */}
                        {(hasPermission('erp_bank_reconciliation', 'read') || hasPermission('erp_exchange_rate', 'read')) && (
                          <div>
                            <button
                              onClick={() => setOpenMobileErpSubmenu(openMobileErpSubmenu === 'banking' ? null : 'banking')}
                              className="w-full flex items-center justify-between py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                            >
                              <span className="font-medium">Banking</span>
                              <svg className={`w-4 h-4 transition-transform ${openMobileErpSubmenu === 'banking' ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openMobileErpSubmenu === 'banking' && (
                              <div className="pl-4 space-y-1">
                                {hasPermission('erp_bank_reconciliation', 'read') && (
                                  <Link 
                                    to="/erp/bank-reconciliation" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Bank Reconciliation
                                  </Link>
                                )}
                                {hasPermission('erp_exchange_rate', 'read') && (
                                  <Link 
                                    to="/erp/exchange-rates" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Exchange Rates
                                  </Link>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Setup & Configuration */}
                        <div>
                          <button
                            onClick={() => setOpenMobileErpSubmenu(openMobileErpSubmenu === 'setup' ? null : 'setup')}
                            className="w-full flex items-center justify-between py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                          >
                            <span className="font-medium">Setup & Configuration</span>
                            <svg className={`w-4 h-4 transition-transform ${openMobileErpSubmenu === 'setup' ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {openMobileErpSubmenu === 'setup' && (
                            <div className="pl-4 space-y-1">
                              {hasPermission('erp_account', 'write') && (
                                <Link 
                                  to="/setup-wizard" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md font-medium"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  ERP Setup Wizard
                                </Link>
                              )}
                              <div className="border-t border-gray-200 my-1"></div>
                              {hasPermission('erp_cost_center', 'read') && (
                                <Link 
                                  to="/erp/cost-centers" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Cost Centers
                                </Link>
                              )}
                              {hasPermission('erp_accounting_period', 'read') && (
                                <Link 
                                  to="/erp/accounting-periods" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Accounting Periods
                                </Link>
                              )}
                              {hasPermission('erp_financial_report', 'read') && (
                                <>
                                  <Link 
                                    to="/erp/document-number-series" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Document Number Series
                                  </Link>
                                  <Link 
                                    to="/erp/print-formats" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Print Formats
                                  </Link>
                                  <Link 
                                    to="/erp/email-templates" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Email Templates
                                  </Link>
                                </>
                              )}
                              {hasPermission('erp_account', 'write') && (
                                <Link 
                                  to="/erp/data-import" 
                                  className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  Data Import
                                </Link>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Advanced Features */}
                        {(hasPermission('erp_project', 'read') || hasPermission('erp_asset', 'read') || hasPermission('erp_payroll', 'read') || hasPermission('erp_subscription', 'read') || hasPermission('erp_commission', 'read')) && (
                          <div>
                            <button
                              onClick={() => setOpenMobileErpSubmenu(openMobileErpSubmenu === 'advanced' ? null : 'advanced')}
                              className="w-full flex items-center justify-between py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                            >
                              <span className="font-medium">Advanced Features</span>
                              <svg className={`w-4 h-4 transition-transform ${openMobileErpSubmenu === 'advanced' ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {openMobileErpSubmenu === 'advanced' && (
                              <div className="pl-4 space-y-1">
                                {hasPermission('erp_project', 'read') && (
                                  <Link 
                                    to="/erp/projects" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Projects
                                  </Link>
                                )}
                                {hasPermission('erp_asset', 'read') && (
                                  <Link 
                                    to="/erp/assets" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Assets
                                  </Link>
                                )}
                                {hasPermission('erp_payroll', 'read') && (
                                  <Link 
                                    to="/erp/payroll" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Payroll
                                  </Link>
                                )}
                                {hasPermission('erp_subscription', 'read') && (
                                  <Link 
                                    to="/erp/subscriptions" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Subscriptions
                                  </Link>
                                )}
                                {hasPermission('erp_commission', 'read') && (
                                  <Link 
                                    to="/erp/commissions" 
                                    className="block py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    Commissions
                                  </Link>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Mobile Operations Menu */}
                {canAccessAdminMenu() && (
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <button
                      onClick={() => setIsMobileOperationsOpen(!isMobileOperationsOpen)}
                      className="w-full flex items-center justify-between px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <span className="text-sm font-medium uppercase tracking-wider text-gray-500">Operations</span>
                      <svg 
                        className={`w-4 h-4 transition-transform ${isMobileOperationsOpen ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isMobileOperationsOpen && (
                      <div className="pl-6">
                        {safeGetAdminMenuItems().map((item) => (
                          <Link 
                            key={item.to}
                            to={item.to} 
                            className="block py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <Link 
                  to="/profile" 
                  className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
                
                {/* Mobile User Info & Logout */}
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="px-3 py-2 text-sm text-gray-700">
                    <div className="font-medium">{user?.firstName} {user?.lastName}</div>
                    <div className="text-gray-500 text-xs">{user?.roles.join(', ')}</div>
                  </div>
                  <button
                    onClick={() => {
                      handleLogout()
                      setIsMobileMenuOpen(false)
                    }}
                    className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    Logout
                  </button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
      
      <main className="max-w-[1400px] mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="overflow-x-hidden">
          <Outlet />
        </div>
      </main>

      {/* Session Expired Modal - TEMPORARILY DISABLED */}
      {/* <SessionExpiredModal 
        isOpen={isSessionExpiredModalOpen} 
        onClose={hideSessionExpiredModal} 
      /> */}
    </div>
  )
}

export default Layout
